/**
 * Registry: `documentKey` (DS) → контекст строки FILEATTACHMENTS для разбора WS.
 *
 * Заполняется при `prepare`; `code`/`version` — для аудита и локального контекста.
 * WS (WUI-346): корреляция по `documentSessionId`, не по `code`/`version`.
 * `storageUri` обновляется из WS (`patchR7OfficeDocumentContextStorageUri`).
 *
 * @see dev/WUI-316.md §7.4
 */
export type R7OfficeDocumentContext = {
  elementId: number
  code: number
  version: number
  storageUri: string
  /** Имя файла для новых локальных строк (code=0). */
  fileName?: string
}

const registry = new Map<string, R7OfficeDocumentContext>()

export function registerR7OfficeDocumentContext(
  documentKey: string,
  context: R7OfficeDocumentContext,
): void {
  const key = documentKey.trim()
  if (!key) return
  if (!Number.isFinite(context.elementId) || context.elementId <= 0) {
    return
  }
  if (!Number.isFinite(context.code) || context.code < 0) {
    return
  }
  registry.set(key, {
    elementId: context.elementId,
    code: context.code,
    version: Number.isFinite(context.version) ? context.version : 0,
    storageUri: context.storageUri.trim(),
    fileName: context.fileName?.trim() || undefined,
  })
}

export function unregisterR7OfficeDocumentContext(documentKey: string): void {
  const key = documentKey.trim()
  if (!key) return
  registry.delete(key)
}

export function resolveR7OfficeDocumentContext(
  documentKey: string,
): R7OfficeDocumentContext | undefined {
  const key = documentKey.trim()
  if (!key) return undefined
  return registry.get(key)
}

/** Обновить `storageUri` после WS callback (copy-on-prepare / новый temp). */
export function patchR7OfficeDocumentContextStorageUri(
  documentKey: string,
  storageUri: string,
): void {
  const key = documentKey.trim()
  const uri = storageUri.trim()
  if (!key || !uri) return
  const ctx = registry.get(key)
  if (!ctx) return
  registry.set(key, { ...ctx, storageUri: uri })
}

/** @internal */
export function __clearR7OfficeDocumentContextRegistryForTests(): void {
  registry.clear()
}
